import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

export type DeleteItemType = 'group' | 'channel' | 'workspace';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  type: DeleteItemType;
  itemName: string;
  warningText?: string;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  itemName,
  warningText
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      await onConfirm();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to delete ${type}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isGroup = type === 'group';
  const isWorkspace = type === 'workspace';
  const typeLabel = isWorkspace ? 'Workspace' : isGroup ? 'Group' : 'Channel';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-destructive/20 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5 font-bold text-base text-destructive">
            <div className="w-8 h-8 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center">
              <Trash2 className="h-4 w-4" />
            </div>
            <span>Delete {typeLabel} Permanently</span>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 text-xs bg-destructive/10 text-destructive border border-destructive/20 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm text-foreground">
              Are you sure you want to delete{' '}
              <strong className="text-foreground font-semibold">
                {isWorkspace ? `workspace "${itemName}"` : isGroup ? itemName : `#${itemName}`}
              </strong>
              ?
            </p>

            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                {warningText || (
                  isWorkspace ? (
                    <span>
                      Deleting this workspace will <strong>permanently remove all channels, groups, messages, meetings, files, and members</strong> associated with it. This action cannot be undone.
                    </span>
                  ) : isGroup ? (
                    <span>
                      Deleting this group will <strong>permanently remove all channels, messages, threads, and files</strong> contained within it. This action cannot be undone.
                    </span>
                  ) : (
                    <span>
                      Deleting this channel will <strong>permanently remove all messages, threads, and reactions</strong> inside it. This action cannot be undone.
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 bg-muted/40 border-t border-border flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-destructive hover:bg-destructive/90 rounded-xl transition flex items-center gap-2 disabled:opacity-50 shadow-sm"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Delete Permanently</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
